import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SortingState } from "@tanstack/react-table";
import { Printer, Search } from "lucide-react";
import { useAtomValue } from "jotai";
import { userAtom } from "@/stores/authStore";
import usePagination from "@/hooks/useSyncedPagination";
import { ActionDataBo } from "@/types/tableAction";
import {
  useGetAllExpeditionBo,
  useGetExpeditionCountsValidatedSummaryBo,
} from "@/api/expedition.api";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { OngoingExpeditionsTable } from "@/components/backOffice/validatedExpeditions/ongoingExpeditionsTable";
import { DeliveredExpeditionsTable } from "@/components/backOffice/validatedExpeditions/deliveredExpeditionsTable";
import { FailedExpeditionsTable } from "@/components/backOffice/validatedExpeditions/failedExpeditionsTable";
import { ChartDayPicker } from "@/components/backOffice/validatedExpeditions/chartDayPicker";
import { ValidatedExpeditionsStatusBox } from "@/components/backOffice/validatedExpeditions/validatedExpeditionsStatusBox";
import { ValidatedExpeditionsFilters } from "@/components/backOffice/validatedExpeditions/validatedExpeditionsFilters";
import {
  useValidatedExpeditionsFilters,
  deliveryStatusGroupToParams,
} from "@/components/backOffice/validatedExpeditions/useValidatedExpeditionsFilters.reducer";
import { COUNTRY_REGIONS } from "@/constants/countries";
import { ValidatedExpeditionsModals } from "@/components/backOffice/validatedExpeditions/validatedExpeditionsModals";
import { ExpeditionTypeEnum } from "@/constants/enums/expeditionType.enum";
import { PrintShippingLabelsModal } from "@/components/backOffice/validatedExpeditions/modal/printShippingLabels.modal";
import { Button } from "@/components/ui/button";

export const ValidatedExpeditionsV2Page = () => {
  const { t } = useTranslation("boExpeditions");
  const user = useAtomValue(userAtom);
  const [actionData, setActionData] = useState<ActionDataBo | null>(null);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: "finalizedAt", desc: true }]);
  const [pagination, setPagination] = usePagination("validatedExpeditionsV2");
  const { filters, debouncedSearch, actions } = useValidatedExpeditionsFilters();

  const [chartMonth, setChartMonth] = useState(() => {
    const from = filters.finalizedAt?.[0] ?? new Date();
    return new Date(from.getFullYear(), from.getMonth(), 1);
  });

  const group = filters.deliveryStatusGroup;

  const handleDeliveryStatusGroupChange = useCallback(
    (nextGroup: typeof group) => {
      actions.setDeliveryStatusGroup(nextGroup);
    },
    [actions],
  );

  useEffect(() => {
    if (filters.finalizedDateFilterType === "MONTH") {
      actions.setFinalizedMonth(true, chartMonth);
    }
    // Only re-sync when the navigated month changes, not on every filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartMonth]);

  const handleToggleMonthFilter = useCallback(() => {
    actions.setFinalizedMonth(filters.finalizedDateFilterType !== "MONTH", chartMonth);
  }, [actions, filters.finalizedDateFilterType, chartMonth]);

  const pickupDateFilter = useMemo(() => {
    if (!filters.pickupDate) return {};
    return {
      pickupDateAfter: filters.pickupDate[0].toISOString(),
      pickupDateBefore: filters.pickupDate[1].toISOString(),
    };
  }, [filters.pickupDate]);

  const finalizedAtFilter = useMemo(() => {
    if (!filters.finalizedAt) return {};
    return {
      finalizedAfter: filters.finalizedAt[0].toISOString(),
      finalizedBefore: filters.finalizedAt[1].toISOString(),
    };
  }, [filters.finalizedAt]);

  const baseQueryFilter = useMemo(
    () => ({
      processStatus: ["VALIDATED"] as string[],
      ...finalizedAtFilter,
      ...deliveryStatusGroupToParams(filters.deliveryStatusGroup),
      ...(filters.isExpress ? { isExpress: true } : {}),
      ...(filters.isRemote ? { isRemote: true } : {}),
      ...(filters.isShippingLate ? { isShippingLate: true } : {}),
      ...(filters.isCustomQuotation ? { quotationType: ExpeditionTypeEnum.CUSTOM_QUOTATION } : {}),
      ...(filters.isFinalizedBySelf ? { finalizedBySelf: true } : {}),
      ...(filters.finalizedById ? { finalizedById: filters.finalizedById } : {}),
      ...(filters.country
        ? { country: filters.country }
        : filters.region
          ? { countries: COUNTRY_REGIONS[filters.region] }
          : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(filters.searchExact ? { searchExact: true } : {}),
      ...(filters.customId ? { customId: filters.customId } : {}),
      ...(filters.packagingType ? { packagingType: filters.packagingType } : {}),
      ...(filters.manualTracking
        ? { manualTracking: filters.manualTracking === "manualTracking" }
        : {}),
      ...pickupDateFilter,
    }),
    [
      finalizedAtFilter,
      pickupDateFilter,
      filters.deliveryStatusGroup,
      filters.isExpress,
      filters.isRemote,
      filters.isShippingLate,
      filters.isCustomQuotation,
      filters.isFinalizedBySelf,
      filters.finalizedById,
      filters.country,
      filters.region,
      debouncedSearch,
      filters.searchExact,
      filters.customId,
      filters.packagingType,
      filters.manualTracking,
    ],
  );

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [baseQueryFilter, setPagination]);

  const { data: staticCountsSummary } = useGetExpeditionCountsValidatedSummaryBo({
    processStatus: ["VALIDATED"],
  });
  const { data: deletedCountData } = useGetAllExpeditionBo({ isDeleted: true, take: 1 });

  const { data: countsSummary } = useGetExpeditionCountsValidatedSummaryBo(baseQueryFilter);

  const { data: expeditions, isLoading: isExpeditionsLoading } = useGetAllExpeditionBo({
    ...baseQueryFilter,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? "DESC" : "ASC",
  });

  const expeditionsData = useMemo(() => expeditions?.data ?? [], [expeditions]);

  return (
    <div className="bg-border/20 flex h-fit min-h-dvh w-full flex-1 flex-col gap-2 p-8">
      <div className="flex w-full flex-col justify-between gap-2 md:flex-row">
        <div className="flex w-fit flex-col gap-2">
          <h1 className="font-jakarta-sans text-3xl font-semibold">{t("validatedPage.title")}</h1>
          <p>{t("validatedPage.subtitle")}</p>
        </div>
        <div className="flex flex-col items-end gap-4 py-2">
          <div className="flex w-72 flex-row items-center gap-2">
            <UnifiedInput
              inputType="text"
              leftElement={<Search />}
              value={filters.search}
              onChange={(e) => actions.setSearch(String(e || ""))}
              className="placeholder:text-[#94A3B8]"
              placeholder={t("validatedPage.searchPlaceholder")}
            />
            <Button variant="outline" onClick={() => setPrintModalOpen(true)} className="h-10">
              <Printer className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>
      <ChartDayPicker
        currentMonth={chartMonth}
        onMonthChange={setChartMonth}
        selectedDay={
          filters.finalizedDateFilterType === "DAY" ? filters.finalizedAt?.[0] ?? null : null
        }
        onSelectedDayChange={actions.setFinalizedDay}
        isActive={filters.finalizedDateFilterType === "MONTH"}
        onToggle={handleToggleMonthFilter}
      />
      <ValidatedExpeditionsStatusBox
        deliveryStatusGroup={filters.deliveryStatusGroup}
        onDeliveryStatusGroupChange={handleDeliveryStatusGroupChange}
        counts={staticCountsSummary?.quickFilters}
        deletedCount={deletedCountData?.count}
        isSuperadmin={user?.role === "SUPERADMIN"}
      />
      <ValidatedExpeditionsFilters
        group={group}
        filters={filters}
        actions={actions}
        counts={countsSummary?.quickFilters}
      />
      {/* <div className="rounded-xl border-1 bg-white p-4 sm:p-8"> */}
      {(() => {
        const tableProps = {
          data: expeditionsData,
          rowCount: expeditions?.count || 0,
          pagination,
          setPagination,
          sorting,
          setSorting,
          setActionData,
          isLoading: isExpeditionsLoading,
        };
        switch (group) {
          case "ongoing":
            return <OngoingExpeditionsTable {...tableProps} />;
          case "delivered":
            return <DeliveredExpeditionsTable {...tableProps} />;
          case "failed":
            return <FailedExpeditionsTable {...tableProps} />;
          default:
            return null;
        }
      })()}
      {/* </div> */}
      <ValidatedExpeditionsModals
        actionData={actionData}
        onClose={() => setActionData(null)}
        setActionData={setActionData}
        isDeleted={false}
      />
      {printModalOpen && (
        <PrintShippingLabelsModal open={true} onClose={() => setPrintModalOpen(false)} />
      )}
    </div>
  );
};
